import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, CardTitle, Alert, Spinner, Button } from 'reactstrap';
import { useAuth } from '../auth/AuthProvider';
import { getZitadelClient } from '../auth/zitadelClient';
import { createDebugLogger } from '../utils/debug';

const dlog = createDebugLogger('oidc-callback');

/**
 * OIDC-Callback-Handler.
 *
 * Per Spec orbit-specs/zitadel-auth-integration.md §UX:
 *  - Zitadel redirected hierher mit ?code=…&state=…
 *  - signinRedirectCallback() macht den Code-Exchange (PKCE)
 *  - Bei Erfolg: Tokens in AuthProvider, redirect zu state.returnTo
 *
 * Doppel-Exchange-Schutz: useRef-Guard, weil React.StrictMode den Effekt
 * in Dev zweimal ausführt (zweiter Exchange würde mit invalid_grant
 * fehlschlagen).
 */
export function LoginCallbackPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      try {
        const mgr = getZitadelClient();
        const user = await mgr.signinRedirectCallback();
        dlog('exchanged', { hasAccess: !!user.access_token, hasRefresh: !!user.refresh_token });
        if (!user?.access_token) throw new Error('no_access_token_in_response');
        auth.setTokens({
          accessToken: user.access_token,
          refreshToken: user.refresh_token ?? null,
        });
        // Open-Redirect-Schutz: protocol-relative URLs (//evil.com) blockieren.
        // OIDC state ist user-controlled, kommt 1:1 wieder zurück.
        const raw = typeof user.state === 'string' ? user.state : '';
        const target = raw.startsWith('/') && !raw.startsWith('//') ? raw : '/';
        navigate(target, { replace: true });
      } catch (err) {
        dlog('callback failed', err);
        setError(err?.message ?? 'OIDC-Callback fehlgeschlagen.');
      }
    })();
  }, [auth, navigate]);

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
      <Card style={{ maxWidth: 480, width: '100%' }}>
        <CardBody>
          <CardTitle tag="h4">Anmeldung…</CardTitle>
          {!error ? (
            <div className="d-flex align-items-center gap-3">
              <Spinner size="sm" /> <span>Token wird ausgetauscht…</span>
            </div>
          ) : (
            <>
              <Alert color="danger" className="mb-3">
                <strong>Anmeldung fehlgeschlagen:</strong> {error}
              </Alert>
              <Button color="primary" onClick={() => navigate('/login', { replace: true })}>
                Zurück zum Login
              </Button>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
